import { lazy, type ComponentType } from "react";
import { Sentry } from "@/lib/sentry";

// ─────────────────────────────────────────────────────────────────────────────
// Hardened React.lazy wrapper (Task #3997)
//
// The recurring production crash
//   `TypeError: undefined is not an object (evaluating 's._result.default')`
//   / `Cannot read properties of undefined (reading 'default')`
// comes from React 19's `lazyInitializer` reading `result.default` on a module
// that a `React.lazy()` factory resolved to WITHOUT a usable `default` export.
// That happens when a code-split chunk is stale/failed after a deploy, a
// transient CDN/proxy hiccup serves garbage, or the loader hands back
// `undefined`. The previous wrapper only recovered when the import *rejected* —
// it did nothing when the import *resolved to an invalid module*, which is the
// exact crash class above.
//
// This wrapper:
//   1. VALIDATES every resolved import actually yields a usable component
//      (a defined module with a usable `default`, or — via `pick` — a usable
//      named export remapped to default). An invalid resolve is treated the
//      same as a rejection.
//   2. RETRIES once in-memory (handles a transient network blip / race), then
//      performs a one-time cache-busted full-page reload so the browser fetches
//      the fresh index.html and the new hashed chunk. The reload is guarded
//      PER CHUNK with a short TTL so it can never get stuck in a loop and one
//      failing chunk never suppresses retries for unrelated chunks.
//   3. On a terminal (post-reload) failure, captures the failing import
//      path/route to Sentry BEFORE surfacing the error, so the residual
//      occurrence is attributable, then rethrows a chunk-load error the
//      top-level ErrorBoundary recognises and offers a true reload for.
// ─────────────────────────────────────────────────────────────────────────────

type AnyComponent = ComponentType<any>;
type DefaultModule = { default: AnyComponent };

export interface LazyRetryOptions {
  /**
   * Remap a named export to `default`. Supports the Stripe `Elements` /
   * Lottie `DotLottieReact` call sites that previously did
   * `import(...).then((m) => ({ default: m.X }))` by hand. Pass `pick: "X"`.
   */
  pick?: string;
  /**
   * Stable identifier for the imported chunk, used for the per-chunk reload
   * guard key and Sentry attribution. Defaults to the import specifier parsed
   * out of the factory source; pass explicitly when that can't be derived.
   */
  name?: string;
}

const RELOAD_GUARD_PREFIX = "lazy_reload:";
// A reload that does not fix the chunk within this window is treated as a
// terminal failure (friendly reload UI). Older guards are stale (e.g. a fresh
// deploy days later) and are cleared so recovery can run again.
const RELOAD_GUARD_TTL_MS = 20_000;
const CACHE_BUST_PARAM = "_cb";

/**
 * Error thrown for a resolved-but-invalid lazy import (the `_result.default`
 * class) and for the terminal post-reload failure. Recognised by
 * `isChunkLoadError` so the error boundary can offer a true reload.
 */
export class ChunkLoadError extends Error {
  constructor(
    message: string,
    readonly chunkName: string,
    options?: { cause?: unknown },
  ) {
    super(message);
    this.name = "ChunkLoadError";
    if (options?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

function deriveName(factory: () => Promise<unknown>, explicit?: string): string {
  if (explicit) return explicit;
  try {
    const m = factory.toString().match(/import\(\s*["'`]([^"'`]+)["'`]\s*\)/);
    if (m?.[1]) return m[1];
  } catch {
    /* toString can throw under exotic bundlers — fall through */
  }
  return "unknown";
}

/**
 * Validate the resolved module and remap a named export to `default` when a
 * `pick` is requested. Returns `null` when the module is unusable (the crash
 * class we must intercept instead of letting React read `_result.default`).
 */
function pickModule(mod: unknown, pick?: string): DefaultModule | null {
  if (mod == null || typeof mod !== "object") return null;
  const record = mod as Record<string, unknown>;
  if (pick) {
    const fromNamed = record[pick];
    const nested = record.default as Record<string, unknown> | undefined;
    const candidate = fromNamed ?? (nested ? nested[pick] : undefined);
    return candidate != null
      ? { default: candidate as AnyComponent }
      : null;
  }
  return record.default != null
    ? { default: record.default as AnyComponent }
    : null;
}

function readReloadGuard(key: string): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) {
      sessionStorage.removeItem(key);
      return false;
    }
    if (Date.now() - ts > RELOAD_GUARD_TTL_MS) {
      sessionStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function writeReloadGuard(key: string): void {
  try {
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    /* sessionStorage can be unavailable (private mode / blocked) */
  }
}

function clearReloadGuard(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Full-page reload with a cache-busting query param so a stale CDN/proxy copy
 * of the document (and therefore the stale chunk references it points at) can't
 * be re-served. The param is stripped on the next boot (see module init below)
 * so it never pollutes the URL the user sees or analytics.
 */
function reloadWithCacheBust(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    try {
      window.location.reload();
    } catch {
      /* nothing more we can do */
    }
  }
}

// Matches a real built chunk specifier (e.g. `./DashboardPage-CcecDVQD.js`),
// which is what `deriveName` parses out of the production factory. In dev the
// name is an alias like `@/pages/Foo` (nothing fetchable), so this skips it.
const BUILT_CHUNK_RE = /\.(?:js|mjs)(?:\?|#|$)/i;
const REFETCH_TIMEOUT_MS = 4_000;

/**
 * Force the browser to re-download a failing chunk while BYPASSING the HTTP
 * cache (`cache: "reload"`), overwriting the cached entry before we reload.
 *
 * Hashed chunks are served `immutable`, so a corrupt/truncated copy in the
 * device cache (e.g. an interrupted download on mobile Safari that lost the
 * trailing `export{… as default}`) makes the module resolve WITHOUT a usable
 * default on every load. The document-level cache-bust reload re-references the
 * SAME immutable chunk URL and re-reads the poisoned entry, so it can never
 * recover — the exact reload loop behind DRIVESHARE_V2-7G. Re-fetching the
 * subresource itself with a cache-reload repopulates a good copy so the import
 * after the reload succeeds. Best-effort: never throws, never blocks recovery.
 */
async function refetchChunkBypassingCache(name: string): Promise<void> {
  if (!BUILT_CHUNK_RE.test(name)) return;
  if (typeof fetch !== "function") return;
  let url: string;
  try {
    // The factory specifier is relative to the chunk that contains the
    // `import()` — all hashed chunks live in the same assets dir as this
    // module, so resolve against `import.meta.url`.
    url = new URL(name, import.meta.url).href;
  } catch {
    return;
  }
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), REFETCH_TIMEOUT_MS)
    : null;
  try {
    await fetch(url, {
      cache: "reload",
      credentials: "same-origin",
      signal: controller?.signal,
    });
  } catch {
    /* best-effort: a failed refetch must not block the reload below */
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Re-import the failing chunk under a DISTINCT, cache-busted URL so the browser
 * fetches a fresh module instance instead of replaying the poisoned one.
 *
 * A document reload (`reloadWithCacheBust`) re-references the SAME immutable
 * chunk URL, so the browser re-reads the same poisoned HTTP-cache entry AND the
 * same parsed entry in its in-memory module map — on mobile Safari that can
 * never recover and just loops until the TTL terminal fires (DRIVESHARE_V2-7G).
 * Appending a cache-bust query makes a brand-new module-map key, so the import
 * resolves to a freshly fetched copy and the page can recover WITHOUT a jarring
 * full-page reload. Best-effort: returns `null` (never throws) so the caller can
 * fall through to the reload path.
 */
async function reimportFresh(
  name: string,
  pick?: string,
): Promise<DefaultModule | null> {
  // Only real built chunk specifiers are fetchable; a dev alias (`@/pages/Foo`)
  // is nothing the browser can import directly.
  if (!BUILT_CHUNK_RE.test(name)) return null;
  if (typeof window === "undefined") return null;
  let url: string;
  try {
    const u = new URL(name, import.meta.url);
    u.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
    url = u.href;
  } catch {
    return null;
  }
  try {
    const mod = await import(/* @vite-ignore */ url);
    return pickModule(mod, pick);
  } catch {
    return null;
  }
}

/**
 * Record a successful recovery as an info breadcrumb instead of a hard error so
 * the residual occurrence is observable (attributable to a stale/poisoned chunk)
 * without being double-counted as a crash in Sentry. Never throws.
 */
function recordRecoveryBreadcrumb(name: string, method: string): void {
  try {
    Sentry.addBreadcrumb({
      category: "lazyRetry",
      level: "info",
      message: `lazyRetry recovered "${name}" via ${method}`,
    });
  } catch {
    /* never let reporting affect the successful render */
  }
}

// Strip the cache-bust param left by a recovery reload so it never lingers in
// the address bar or feeds into routing/analytics. Runs once at module load.
if (typeof window !== "undefined") {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has(CACHE_BUST_PARAM)) {
      u.searchParams.delete(CACHE_BUST_PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        u.pathname + u.search + u.hash,
      );
    }
  } catch {
    /* ignore */
  }
}

const CHUNK_ERROR_PATTERNS = [
  /_result\.default/i,
  /reading 'default'/i,
  /evaluating '.*\.default'/i,
  /Loading chunk .* failed/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /dynamically imported module/i,
];

/**
 * True when an error is a code-split chunk-load failure (including the
 * resolved-but-invalid `_result.default` class). The error boundary uses this
 * to offer a true full-page reload instead of a React state reset (which cannot
 * recover a missing chunk).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof ChunkLoadError) return true;
  const err = error as { name?: unknown; message?: unknown };
  if (err.name === "ChunkLoadError") return true;
  const message = typeof err.message === "string" ? err.message : "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

export function lazyRetry(
  factory: () => Promise<unknown>,
  options: LazyRetryOptions = {},
) {
  const name = deriveName(factory, options.name);
  const guardKey = RELOAD_GUARD_PREFIX + name;

  return lazy<AnyComponent>(async () => {
    const attempt = async (): Promise<DefaultModule> => {
      const mod = await factory();
      const resolved = pickModule(mod, options.pick);
      if (resolved) return resolved;
      // Resolved but unusable — this is the `_result.default` crash class.
      // Throw so it follows the same recovery path as a rejected import,
      // instead of handing React a module it will crash reading.
      throw new ChunkLoadError(
        `lazyRetry: import "${name}" resolved without a usable ${
          options.pick ? `"${options.pick}" export` : "default export"
        }`,
        name,
      );
    };

    try {
      const resolved = await attempt();
      // When we land here AFTER a recovery reload happened for this chunk, the
      // reload fixed it — downgrade to a breadcrumb instead of letting the
      // earlier failure read as a hard crash.
      if (readReloadGuard(guardKey)) recordRecoveryBreadcrumb(name, "reload");
      clearReloadGuard(guardKey);
      return resolved;
    } catch {
      // One in-memory retry first — recovers a transient network blip / race
      // without a jarring full-page reload.
      let lastErr: unknown;
      try {
        const resolved = await attempt();
        if (readReloadGuard(guardKey)) recordRecoveryBreadcrumb(name, "reload");
        clearReloadGuard(guardKey);
        return resolved;
      } catch (err) {
        lastErr = err;
      }

      // Silent recovery WITHOUT a full-page reload: re-import the chunk under a
      // cache-busted URL so the browser fetches a fresh module instance instead
      // of replaying the poisoned HTTP-cache / module-map entry (a plain reload
      // re-references the same immutable URL and can loop forever on mobile
      // Safari — DRIVESHARE_V2-7G). Most stale/poisoned-chunk hits recover here.
      const fresh = await reimportFresh(name, options.pick);
      if (fresh) {
        clearReloadGuard(guardKey);
        recordRecoveryBreadcrumb(name, "cache-busted re-import");
        return fresh;
      }

      if (!readReloadGuard(guardKey)) {
        // First persistent failure for this chunk: cache-busted reload to
        // pick up the fresh bundle. Keep Suspense pending while we navigate.
        writeReloadGuard(guardKey);
        // Overwrite any corrupt/truncated immutable-cached copy of the chunk
        // first; a document-only reload re-reads the same poisoned subresource
        // and can never recover otherwise (DRIVESHARE_V2-7G).
        await refetchChunkBypassingCache(name);
        reloadWithCacheBust();
        return new Promise<DefaultModule>(() => {});
      }
      // Terminal: a reload already happened and the chunk still won't load.
      // Clear the guard (so a future legitimate deploy can recover) and
      // surface a chunk-load error the ErrorBoundary recognises — after
      // recording it for attribution.
      clearReloadGuard(guardKey);
      const err = lastErr;
      const terminal =
        err instanceof ChunkLoadError
          ? err
          : new ChunkLoadError(
              `lazyRetry: import "${name}" failed to load after reload`,
              name,
              { cause: err },
            );
      try {
        Sentry.captureException(terminal, {
          tags: { boundary: "lazyRetry", lazy_chunk: name },
          extra: {
            importName: name,
            route:
              typeof window !== "undefined"
                ? window.location.pathname
                : undefined,
            originalError: err instanceof Error ? err.message : String(err),
          },
        });
      } catch {
        /* never let reporting block surfacing the error */
      }
      throw terminal;
    }
  });
}
